import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FaUser, FaUsers, FaSignOutAlt, FaChevronDown, FaHome, FaFilter, FaEnvelope } from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import { useUserProfile } from '../../hooks/useUserProfile';
import { useNotificationChecker } from '../../hooks/useNotificationChecker';
import { useUserFilter } from '../../context/UserFilterContext';
import UserSelector from '../admin/UserSelector';
import { NotificationBell } from '../notifications/NotificationBell';
import { ActivityIcon } from '../activity/ActivityIcon';
import { usersService } from '../../services/supabase/users.service';
import { User } from '../../types/user';
import useWindowSize from '../../hooks/useWindowSize';
import { HeaderAgencyLogo } from './AgencyLogo';
import { NavigationDropdown } from '../common/NavigationDropdown';

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  background-color: ${props => props.theme.colors.background};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  z-index: 3000; /* Augmenté pour être au-dessus de tout (carte, searchbar, boutons) */
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  
  @media (max-width: 768px) {
    padding: 0 12px;
    height: 56px; /* Un peu plus petit sur mobile */
  }
  
  @media (max-width: 480px) {
    padding: 0 8px;
    height: 52px; /* Encore plus petit sur très petits écrans */
  }
`;

// Section gauche : Logo + Navigation
const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 24px; /* Réduit pour plus d'espace mais pas trop */
  flex: 1; /* Prendre plus d'espace sur mobile */
  
  @media (max-width: 768px) {
    gap: 16px; /* Un peu plus d'espace sur mobile */
    flex: 1;
  }
  
  @media (max-width: 480px) {
    gap: 12px; /* Maintenir un espacement décent */
    flex: 1;
  }
`;

const _LogoPlaceholder = styled.div`
  width: 120px;
  height: 40px;
  background-color: ${props => props.theme.colors.border};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.textLight};
  font-size: 14px;
  font-weight: 500;
  
  @media (max-width: 768px) {
    width: 80px;
    height: 32px;
    font-size: 12px;
  }
  
  @media (max-width: 480px) {
    width: 60px;
    height: 28px;
    font-size: 10px;
  }
`;

// Section droite : Icônes
const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  
  @media (max-width: 768px) {
    gap: 8px; /* Réduit pour plus d'espace */
  }
  
  @media (max-width: 480px) {
    gap: 6px; /* Encore plus réduit */
  }
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  
  @media (max-width: 768px) {
    gap: 8px; /* Réduit pour plus d'espace */
  }
  
  @media (max-width: 480px) {
    gap: 6px; /* Encore plus réduit */
  }
`;

// Menu utilisateur (repris du MainApp)
const UserMenuContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const ProfileButton = styled.button<{ $isFiltered?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: ${props => props.$isFiltered ? props.theme.colors.secondary : props.theme.colors.primary};
  color: white;
  border: none;
  border-radius: 20px;
  padding: 6px 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  @media (max-width: 768px) {
    padding: 6px 8px;
  }
  
  .user-name {
    transition: opacity 0.2s ease;
  }
  
  &:hover {
    background-color: ${props => props.$isFiltered ? props.theme.colors.secondaryDark : props.theme.colors.primaryDark};
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }
`;

const DropdownMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  width: 220px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: ${props => props.$isOpen ? 'block' : 'none'};
  margin-top: 8px;
  z-index: 1000;
  max-height: 80vh;
  overflow-y: auto;
  overflow-x: hidden;
  color: #000000 !important;
  font-weight: 500 !important;
  text-shadow: 0 0 1px rgba(255,255,255,0.5);
  
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.3) transparent;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.3);
    border-radius: 6px;
  }
  
  @media (max-height: 600px) {
    max-height: 70vh;
  }
  
  @media (max-height: 480px) {
    max-height: 60vh;
  }
`;

const MenuItem = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${props => props.theme.colors.text};
  gap: 12px;
  font-size: 14px;
  border-radius: 4px;
  margin: 2px 4px;
  
  &:hover {
    background-color: ${props => props.theme.colors.backgroundLight};
    transform: translateX(2px);
  }
  
  &:active {
    transform: scale(0.98);
  }
  
  svg {
    color: ${props => props.theme.colors.primary};
    min-width: 16px;
  }
  
  @media screen and (max-width: 768px) {
    color: #000000 !important;
    font-weight: 500 !important;
    text-shadow: 0 0 1px rgba(255,255,255,0.5);
    padding: 14px 16px;
    
    svg {
      color: #0066cc !important;
      filter: drop-shadow(0 0 1px rgba(255,255,255,0.5));
    }
  }
  
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MenuDivider = styled.div`
  height: 1px;
  background-color: ${props => props.theme.colors.border};
  margin: 8px 4px;
  opacity: 0.7;
  border-radius: 1px;
  box-shadow: 0 0 1px rgba(0, 0, 0, 0.05);
`;

interface UserAvatarProps {
  $imageUrl?: string | null;
  $borderColor?: string | null;
  $initials?: string;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
}

const UserAvatar = styled.div.attrs<UserAvatarProps>(({ onClick }) => ({
  onClick
}))<UserAvatarProps>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${({ theme, $imageUrl, $borderColor }) => $imageUrl ? 'transparent' : ($borderColor || theme.colors.primaryLight)};
  background-image: ${({ $imageUrl }) => $imageUrl ? `url(${$imageUrl})` : 'none'};
  background-size: cover;
  background-position: center;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  font-weight: bold;
  margin-right: 8px;
  border: 2px solid ${({ $borderColor, theme }) => $borderColor || theme.colors.primary};
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: visible;
  z-index: 1;
  
  /* Réduire uniquement sur mobile */
  @media (max-width: 768px) {
    width: 30px;
    height: 30px;
    font-size: 0.875rem;
    margin-right: 6px;
  }
  
  /* Afficher les initiales seulement s'il n'y a pas d'image */
  ${({ $imageUrl, $initials }) => !$imageUrl && `
    &::after {
      content: "${$initials || ''}";
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: bold;
      z-index: 2;
    }
  `}
  
  &:hover {
    transform: scale(1.1);
  }
`;

interface HeaderProps {
  activeView: 'prospection' | 'crm';
  onViewChange: (view: 'prospection' | 'crm') => void;
  onActivityClick: () => void;
}

const NotificationWrapper = () => {
  useNotificationChecker();
  return <NotificationBell />;
};

const Header: React.FC<HeaderProps> = ({ 
  activeView, 
  onViewChange, 
  onActivityClick 
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserSelectorOpen, setIsUserSelectorOpen] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [selectedUserInfo, setSelectedUserInfo] = useState<User | null>(null);
  const [hasMultipleUsers, setHasMultipleUsers] = useState(false);
  const navigate = useNavigate();
  const { supabaseUser, signOut } = useAuth();
  const { userProfile } = useUserProfile();
  const { selectedUserId, setSelectedUserId } = useUserFilter();
  const { isMobile } = useWindowSize();
  
  const isOwner = userProfile?.is_owner || false;
  
  // Vérifier s'il y a plusieurs utilisateurs dans l'agence
  useEffect(() => {
    const checkUsersCount = async () => {
      if (isOwner) {
        try {
          const { data, error } = await usersService.getUsersByAgency();
          if (!error && data) {
            setHasMultipleUsers(data.length > 1);
          }
        } catch (error) {
          console.error('Erreur lors de la vérification du nombre d\'utilisateurs:', error);
        }
      }
    };
    
    checkUsersCount();
  }, [isOwner]);
  
  // Récupérer les informations de l'utilisateur sélectionné
  useEffect(() => {
    const fetchSelectedUserInfo = async () => {
      if (!selectedUserId || (userProfile && selectedUserId === userProfile.id)) {
        setSelectedUserInfo(null);
        return;
      }
      
      try {
        const userInfo = await usersService.getUserProfile(selectedUserId);
        setSelectedUserInfo(userInfo);
      } catch (error) {
        console.error('Erreur lors de la récupération des informations de l\'utilisateur sélectionné:', error);
        setSelectedUserInfo(null);
      }
    };
    
    fetchSelectedUserInfo();
  }, [selectedUserId, userProfile]);
  
  // Gérer le clic en dehors du menu utilisateur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const container = document.getElementById('header-user-menu-container');
      if (container && !container.contains(event.target as Node) && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);
  
  const handleProfileClick = useCallback(() => {
    setIsMenuOpen(!isMenuOpen);
  }, [isMenuOpen]);
  
  const handleProfilePageClick = useCallback(() => {
    setIsMenuOpen(false);
    navigate('/profile');
  }, [navigate]);
  
  const handleBackToAppClick = useCallback(() => {
    setIsMenuOpen(false);
    navigate('/');
  }, [navigate]);
  
  const handleSignOutClick = useCallback(() => {
    setIsMenuOpen(false);
    if (window.confirm('Êtes-vous sûr de vouloir vous déconnecter ?')) {
      sessionStorage.removeItem('immo-prospect-session-started');
      localStorage.removeItem('immo-prospect-selected-user-id');
      signOut();
    }
  }, [signOut]);

  const handleFeedbackClick = useCallback(() => {
    setIsMenuOpen(false);
    setIsFeedbackOpen(true);
  }, []);
  
  // Obtenir les initiales de l'utilisateur (logique du MainApp)
  const getUserInitials = () => {
    if (selectedUserId === null && isOwner && userProfile) {
      return '';
    }
    
    if (selectedUserInfo) {
      if (selectedUserInfo.first_name && selectedUserInfo.name) {
        return `${selectedUserInfo.first_name[0]}${selectedUserInfo.name[0]}`.toUpperCase();
      } else if (selectedUserInfo.email) {
        return selectedUserInfo.email[0].toUpperCase();
      } else {
        return 'U';
      }
    }
    
    if (!userProfile) {
      return supabaseUser?.email ? supabaseUser.email[0].toUpperCase() : 'U';
    }
    
    if (userProfile.first_name && userProfile.name) {
      return `${userProfile.first_name[0]}${userProfile.name[0]}`.toUpperCase();
    } else if (userProfile.email) {
      return userProfile.email[0].toUpperCase();
    } else {
      return supabaseUser?.email ? supabaseUser.email[0].toUpperCase() : 'U';
    }
  };
  
  // Déterminer le nom à afficher
  const getDisplayName = () => {
    if (selectedUserId === null && isOwner && userProfile && hasMultipleUsers) {
      return 'Tous';
    }
    
    if (selectedUserInfo) {
      if (selectedUserInfo.first_name && selectedUserInfo.name) {
        return `${selectedUserInfo.first_name} ${selectedUserInfo.name}`;
      } else if (selectedUserInfo.first_name) {
        return selectedUserInfo.first_name;
      } else if (selectedUserInfo.name) {
        return selectedUserInfo.name;
      } else {
        return 'Utilisateur';
      }
    }
    
    if (userProfile) {
      if (userProfile.first_name && userProfile.name) {
        return `${userProfile.first_name} ${userProfile.name}`;
      } else if (userProfile.first_name) {
        return userProfile.first_name;
      } else if (userProfile.name) {
        return userProfile.name;
      }
    }
    
    return 'Utilisateur';
  };

  return (
    <HeaderContainer>
      <LeftSection>
        <HeaderAgencyLogo />
        
        {/* Menu déroulant pour desktop et mobile */}
        <NavigationDropdown 
          activeView={activeView}
          onViewChange={onViewChange}
        />
      </LeftSection>
      
      <RightSection>
        <IconContainer>
          <ActivityIcon onClick={onActivityClick} />
          <NotificationWrapper />
          
          <UserMenuContainer id="header-user-menu-container" className="user-menu-container">
            <ProfileButton 
              onClick={handleProfileClick} 
              aria-label="Menu utilisateur"
              aria-expanded={isMenuOpen}
              $isFiltered={!!selectedUserInfo || (selectedUserId === null && isOwner)}
            >
              {selectedUserId === null && isOwner && hasMultipleUsers ? (
                <div style={{ 
                  width: '40px', 
                  height: '40px', 
                  borderRadius: '50%', 
                  backgroundColor: '#4285F4',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  marginRight: '8px'
                }}>
                  <FaUsers size={16} />
                </div>
              ) : (
                <UserAvatar 
                  $imageUrl={selectedUserInfo ? selectedUserInfo.photo_url : userProfile?.photo_url}
                  $borderColor={selectedUserInfo ? selectedUserInfo.color : (userProfile?.color || null)}
                  $initials={getUserInitials()}
                  aria-label={getDisplayName()}
                />
              )}
              
              {!isMobile && (
                <span className="user-name">{getDisplayName()}</span>
              )}
              <FaChevronDown size={12} />
            </ProfileButton>
            
            <DropdownMenu $isOpen={isMenuOpen}>
              <MenuItem onClick={handleBackToAppClick}>
                <FaHome size={16} />
                Accueil
              </MenuItem>
              <MenuItem onClick={handleProfilePageClick}>
                <FaUser size={16} />
                Mon profil
              </MenuItem>
              <MenuItem onClick={handleFeedbackClick}>
                <FaEnvelope size={16} />
                Donner mon avis
              </MenuItem>
              
              {isOwner && (
                <>
                  {hasMultipleUsers && (
                    <MenuItem 
                      onClick={() => {
                        setIsUserSelectorOpen(!isUserSelectorOpen);
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <FaFilter size={16} style={{ marginRight: '8px' }} />
                          Filtrer par utilisateur
                        </div>
                        {selectedUserId && (
                          <div 
                            style={{ 
                              width: '10px', 
                              height: '10px', 
                              borderRadius: '50%', 
                              backgroundColor: '#4285F4',
                              marginLeft: '8px'
                            }} 
                          />
                        )}
                      </div>
                    </MenuItem>
                  )}
                  
                  {isUserSelectorOpen && hasMultipleUsers && (
                    <div style={{ padding: '0 8px 8px 8px' }}>
                      <UserSelector 
                        onSelectUser={(userId) => {
                          setSelectedUserId(userId);
                          setIsUserSelectorOpen(false);
                          setIsMenuOpen(false);
                        }}
                        selectedUserId={selectedUserId}
                        inlineStyle={true}
                      />
                    </div>
                  )}
                </>
              )}
              
              <MenuDivider />
              
              <MenuItem onClick={handleSignOutClick}>
                <FaSignOutAlt size={16} />
                Déconnexion
              </MenuItem>
            </DropdownMenu>
          </UserMenuContainer>
        </IconContainer>
      </RightSection>
    </HeaderContainer>
  );
};

export default Header;
